import React, { FC } from "react"
import { ErrorMessage } from "formik"
import {
  GooglePlace,
  LoSearchParams,
  SubscriptionStatus,
  CreateSubscriptionPayload
} from "../../../../../../types"
import AddressAutocomplete from "../../../../address-autocomplete"
import Form from "../../../../form"
import {
  convertAddressObjToPlaceString,
  shouldShowLocationDetails
} from "../../../../../utils"
import { ENGAGEMENT_STATUS_OPTIONS } from "../../../../../constants"
import LoanOfficerAutocomplete from "../../../../loan-officer-autocomplete"
import PropertyLocationDetails from "./property-location-details"

interface Props {
  values: CreateSubscriptionPayload
  loSearchParams: LoSearchParams
  onlyAdmins?: boolean
  setFormField: (name: string, value: any) => void
  setGooglePlace: (value: GooglePlace | undefined) => void
  onLoanOfficerChange?: (loid: string) => void
  hideStatusField?: boolean
  googlePlace?: GooglePlace
  emailRenderError?: (msg: string) => React.ReactNode
  phoneRenderError?: (msg: string) => React.ReactNode
}

const ManageEngagementBasic: FC<Props> = ({
  values,
  loSearchParams,
  onlyAdmins = true,
  setFormField,
  setGooglePlace,
  onLoanOfficerChange,
  hideStatusField = false,
  googlePlace,
  emailRenderError,
  phoneRenderError
}) => {
  const currentStatus: SubscriptionStatus =
    values.deleted === "1"
      ? "deleted"
      : values.active === "1"
      ? "active"
      : "disabled"

  const handleChangeStatus = (val: SubscriptionStatus) => {
    switch (val) {
      case "deleted":
        setFormField("deleted", "1")
        setFormField("active", "0")
        break

      case "active":
        setFormField("deleted", "0")
        setFormField("active", "1")
        break

      case "disabled":
        setFormField("deleted", "0")
        setFormField("active", "0")
        break

      default:
        break
    }
  }

  return (
    <div className="py-6">
      <div className="pb-6">
        <LoanOfficerAutocomplete
          onlyAdmins={onlyAdmins}
          searchParams={loSearchParams}
          label="Loan officer"
          selected={values.loid}
          required
          onChange={val => {
            setFormField("loid", val)
            onLoanOfficerChange?.(val)
          }}
          direction="vertical"
        />
        <ErrorMessage
          name="loid"
          component="p"
          className="mt-2 text-sm text-red-600"
        />
      </div>

      <div className="pb-6">
        <Form.Input
          name="email"
          label="Email address"
          placeholder="Ex. john@email.com"
          required
          renderError={emailRenderError}
        />
      </div>
      <div className="grid grid-cols-2 gap-8">
        <Form.Input
          name="first_name"
          label="First name"
          placeholder="Ex. John"
          required
        />
        <Form.Input
          name="last_name"
          label="Last name"
          placeholder="Ex. Doe"
          required
        />
        <Form.PhoneInput
          name="phone"
          label="Phone number"
          placeholder="Ex. (178) 346-7821"
          required
          renderError={phoneRenderError}
        />
        {!hideStatusField && (
          <div>
            <Form.Select
              label="Status"
              name="status"
              selected={currentStatus}
              data={ENGAGEMENT_STATUS_OPTIONS}
              setSelected={handleChangeStatus}
            />
          </div>
        )}
      </div>

      <div className="pt-10">
        <div className="text-xl font-bold pb-6">Property location</div>

        <AddressAutocomplete
          required
          label="Property address"
          name="location"
          initialValue={convertAddressObjToPlaceString({
            city: values.city,
            county: values.county,
            state: values.state,
            street_address: values.street_address,
            zipcode: values.zipcode
          })}
          placeholder="Search for address, city or zipcode"
          onChange={setGooglePlace}
        />

        {shouldShowLocationDetails(
          googlePlace,
          values.city,
          values.state,
          values.zipcode
        ) && (
          <PropertyLocationDetails
            values={values}
            setFormField={setFormField}
          />
        )}
      </div>
    </div>
  )
}

export default ManageEngagementBasic
